/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import { appIdentifiers, pageViewModifiers } from "constants/enums";
import { getPageViewModifier, getPageZoomLevel } from "selectors/zoom";
import { getCurrentPageNumber as getCurrPageNum } from "selectors/pageNumber";
import {
  isSinglePageMode,
  isPageByPageViewMode,
  isContinuousViewMode,
  getTotalPages,
  isTwoColumnViewMode,
  getViewRotationAngle,
  getLatestHitTestResponse,
  isFullScreenMode,
  isFitOneFullPageMode
} from "selectors/documentSelectors";
import { getIsInPlaceMode } from "selectors/viewModeSelectors";
import { initialPageLoadedPromise, secondaryMetadataDonePromise } from "utils/DocumentViewUtils";
import { isNumber } from "utils/APIUtils";
import { constants } from "publicApis/constants";
import { isAllowed } from "utils/DocPermissionsUtils";
import { COPY_TEXT_PERM } from "constants/constants";
import { getPagesViewYPos, getOpenAction, isInitialPageLoaded } from "selectors/pagedViewState";
import { getPageHeightWithMargin } from "selectors/pagedViewSelectors";
import * as Constants from "constants/constants";
import { itemsInView } from "utils/PageUtils";
import { isThereAnySelection } from "dc-preview-utils/src/selectors/touchSelectors";
import { fetchSelectedContent } from "pageContentHandler/selectionHandler";
import { getViewPortHeight, getPagesViewBoundingRect } from "selectors/documentViewSelectors";
import { getPageHandle } from "selectors/pageSelectors";
import { getSelectedTextQuads } from "utils/SelectionUtils";
import docApi from "api";

export const getDocumentStateApis = stores => {
  function getCurrentPageNumber() {
    return initialPageLoadedPromise(stores).then(() => getCurrPageNum(stores));
  }

  function getProbableInitialPage() {
    return secondaryMetadataDonePromise(stores).then(() => getOpenAction(stores).pageIndex + 1);
  }

  function getSelectedContent() {
    return new Promise((resolve, reject) => {
      if (isAllowed(COPY_TEXT_PERM)) {
        initialPageLoadedPromise(stores).then(() => {
          const returnVal = {
            type: constants.SELECTED_CONTENT_TEXT,
            data: ""
          };
          if (isThereAnySelection(stores)) {
            fetchSelectedContent().then(response => {
              returnVal.data = response.data.text;
              resolve(returnVal);
            }, reject);
          } else resolve(returnVal);
        }, reject);
      } else reject();
    });
  }

  function getViewRotation() {
    return initialPageLoadedPromise(stores).then(() => getViewRotationAngle(stores));
  }

  function getViewMode() {
    return initialPageLoadedPromise(stores).then(() => {
      if (isPageByPageViewMode(stores)) return appIdentifiers.PAGE_BY_PAGE;
      if (isContinuousViewMode(stores)) return appIdentifiers.CONTINUOUS;
      if (isSinglePageMode(stores)) return appIdentifiers.SINGLE_PAGE;
      if (getIsInPlaceMode(stores)) return appIdentifiers.IN_PLACE;
      if (isFullScreenMode(stores)) return appIdentifiers.FULL_SCREEN;
      if (isFitOneFullPageMode(stores)) return appIdentifiers.FIT_ONE_FULL_PAGE;
      const pageViewModifier = getPageViewModifier(stores);
      if (isTwoColumnViewMode(stores)) {
        if (pageViewModifier === pageViewModifiers.FIT_PAGE) {
          return appIdentifiers.TWO_COLUMN_FIT_PAGE;
        } else if (pageViewModifier === pageViewModifiers.FIT_WIDTH) {
          return appIdentifiers.TWO_COLUMN_FIT_WIDTH;
        }
        return appIdentifiers.TWO_COLUMN;
      }
      switch (pageViewModifier) {
        case pageViewModifiers.FIT_PAGE:
          return appIdentifiers.FIT_PAGE;
        case pageViewModifiers.FIT_WIDTH:
          return appIdentifiers.FIT_WIDTH;
        case pageViewModifiers.FIXED_WIDTH:
          return appIdentifiers.FIT_WIDTH;
      }
      return "NONE";
    });
  }

  function getCurrentZoomLevel(pageNumber) {
    return new Promise((resolve, reject) => {
      initialPageLoadedPromise(stores)
        .then(() => {
          if (!isNumber(pageNumber) || pageNumber <= 0 || pageNumber > getTotalPages(stores)) {
            reject("Page number is not valid");
          }
          resolve(getPageZoomLevel(stores, pageNumber - 1) * 100);
        })
        .catch(reject);
    });
  }

  function getPagesInView(args) {
    const { minPagePercentToBeInView } = args;
    return itemsInView(
      stores,
      getPagesViewYPos(stores),
      getPageHeightWithMargin,
      Constants.PAGES_TOP_PADDING,
      true,
      minPagePercentToBeInView
    );
  }

  // Get text from doc rects
  function getTextFromDocRects(startPoint, endPoint, richText = false) {
    return initialPageLoadedPromise(stores)
      .then(() => docApi.textSelectionFromPDFCoordinates(startPoint, endPoint))
      .then(() => fetchSelectedContent(richText))
      .then(result => (richText ? result.data : result.data.text));
  }

  function getCurrentContentTypeUnderMouse() {
    return new Promise((resolve, reject) => {
      if (isInitialPageLoaded(stores)) {
        const hitTestResult = getLatestHitTestResponse(stores);
        resolve(hitTestResult);
      }
      reject("AJS not yet initialized.");
    });
  }

  function detectLanguage(...args) {
    return docApi.detectLanguage(...args);
  }

  /**
   *
   * @param {number} pageNumber
   * @returns {{top: number; left: number; right: number, bottom: number } | null }
   */
  function getPageBoundingRect(pageNumber) {
    if (!isNumber(pageNumber) || pageNumber <= 0 || pageNumber > getTotalPages(stores)) {
      return null;
    }
    const pageHandle = getPageHandle(stores, pageNumber - 1);
    if (!pageHandle) return null;
    const { left, right, bottom, top } = pageHandle.getBoundingClientRect();
    return { left, right, bottom, top };
  }

  function transformPageViewRectToDocViewRect(boundsInViewSpace, pageIndex) {
    const pageRef = getPageHandle(stores, pageIndex);

    const pageViewBox = getPagesViewBoundingRect(stores);
    const pageRect = pageRef.getBoundingClientRect();

    const yOffset = pageRect.top - pageViewBox.top;
    const xOffset = pageRect.left - pageViewBox.left;

    return {
      left: boundsInViewSpace.left + xOffset,
      right: boundsInViewSpace.right + xOffset,
      top: boundsInViewSpace.top + yOffset,
      bottom: boundsInViewSpace.bottom + yOffset
    };
  }

  return {
    transformPageViewRectToDocViewRect,
    getCurrentPageNumber,
    getProbableInitialPage,
    getViewMode,
    getViewRotation,
    getCurrentZoomLevel,
    getSelectedContent,
    getPagesInView,
    getViewPortHeight: () => getViewPortHeight(stores),
    getTextFromDocRects,
    getCurrentContentTypeUnderMouse,
    detectLanguage,
    getPagesViewBoundingRect: () => getPagesViewBoundingRect(stores),
    getPageBoundingRect,
    getSelectedTextQuads: () => getSelectedTextQuads(stores)
  };
};
